import type { VehicleType } from '@/types/survey';

/**
 * The 15 official SLTC vehicle categories.
 *
 * `code` is the notation used on the survey forms (MCL, TW, CAR, …) and is what
 * the surveyor recognises, so it is what the counting card shows.
 * `axleConfig` is the axle configuration from the classification table; it is
 * the only reliable way to tell the articulated types (AG3–AG6) apart in the
 * field, so it is printed on the card.
 *
 * NOTE: `id` values are stored in IndexedDB and in D1. Changing an `id` orphans
 * previously recorded counts, so change `name`/`code` freely but treat `id` as
 * permanent once real data exists.
 */
export const VEHICLE_TYPES: VehicleType[] = [
  {
    id: 'mcl',
    code: 'MCL',
    name: 'Motor Cycle',
    shortName: 'Motor Cycle',
    description: 'All motor cycles, scooters, challies, foot cycles with a motor.',
    sortOrder: 1,
    active: true,
  },
  {
    id: 'tw',
    code: 'TW',
    name: 'Three Wheeler',
    shortName: 'Three Wheeler',
    description: 'All three wheel vehicles (petrol or diesel).',
    sortOrder: 2,
    active: true,
  },
  {
    id: 'car',
    code: 'CAR',
    name: 'Car / Jeep',
    shortName: 'Car / Jeep',
    description: 'Cars, jeeps, single and double cabs, small vans (max 2 rows).',
    sortOrder: 3,
    active: true,
  },
  {
    id: 'van',
    code: 'VAN',
    name: 'Van',
    shortName: 'Van',
    description: 'Vans up to 16 passengers: school, office and delivery vans.',
    sortOrder: 4,
    active: true,
  },
  {
    id: 'mbus',
    code: 'MBUS',
    name: 'Medium Bus',
    shortName: 'Medium Bus',
    axleConfig: '1-1 / 1-2',
    description: 'Buses with up to 29 seats, e.g. Mitsubishi Rosa.',
    sortOrder: 5,
    active: true,
  },
  {
    id: 'lbus',
    code: 'LBUS',
    name: 'Large Bus',
    shortName: 'Large Bus',
    axleConfig: '1-2',
    description: 'Buses with more than 29 seats, single or double door.',
    sortOrder: 6,
    active: true,
  },
  {
    id: 'lgv',
    code: 'LGV',
    name: 'Pick Up Truck',
    shortName: 'Pick Up',
    axleConfig: '1-1',
    description: 'Small lorries with two axles and four wheels, crew cabs, large cabs.',
    sortOrder: 7,
    active: true,
  },
  {
    id: 'mg1',
    code: 'MG1',
    name: 'Medium Truck 1',
    shortName: 'Med Truck 1',
    axleConfig: '1-2',
    description: 'Two axles, six wheels, gross weight under 8.5 t.',
    sortOrder: 8,
    active: true,
  },
  {
    id: 'mg2',
    code: 'MG2',
    name: 'Medium Truck 2',
    shortName: 'Med Truck 2',
    axleConfig: '1-2',
    description: 'Two axles, six wheels, gross weight over 8.5 t.',
    sortOrder: 9,
    active: true,
  },
  {
    id: 'hg3',
    code: 'HG3',
    name: 'Heavy Truck',
    shortName: 'Heavy Truck',
    axleConfig: '1-22',
    description: 'Rigid vehicles with three combined axles.',
    sortOrder: 10,
    active: true,
  },
  {
    id: 'ag3',
    code: 'AG3',
    name: 'Articulated (3 Axles)',
    shortName: 'Artic 3',
    axleConfig: '1-2-2',
    description: 'All three axle articulated vehicles.',
    sortOrder: 11,
    active: true,
  },
  {
    id: 'ag4',
    code: 'AG4',
    name: 'Articulated (4 Axles)',
    shortName: 'Artic 4',
    axleConfig: '1-22-2 / 1-2-22',
    description: 'All four axle articulated vehicles.',
    sortOrder: 12,
    active: true,
  },
  {
    id: 'ag5',
    code: 'AG5',
    name: 'Articulated (5 Axles)',
    shortName: 'Artic 5',
    axleConfig: '1-22-22 / 1-2-222',
    description: 'All five axle articulated vehicles.',
    sortOrder: 13,
    active: true,
  },
  {
    id: 'ag6',
    code: 'AG6',
    name: 'Articulated (6 Axles)',
    shortName: 'Artic 6',
    axleConfig: '1-22-222',
    description: 'All six axle articulated vehicles.',
    sortOrder: 14,
    active: true,
  },
  {
    id: 'fvh',
    code: 'FVH',
    name: 'Tractor',
    shortName: 'Tractor',
    axleConfig: '1-1-1',
    description: 'Two wheel tractor with trailer (land master), or four wheel tractor.',
    sortOrder: 15,
    active: true,
  },
];

/** Active types in display order. */
export const ACTIVE_VEHICLE_TYPES: VehicleType[] = VEHICLE_TYPES.filter((v) => v.active).sort(
  (a, b) => a.sortOrder - b.sortOrder,
);

export const VEHICLE_TYPE_BY_ID: ReadonlyMap<string, VehicleType> = new Map(
  VEHICLE_TYPES.map((v) => [v.id, v]),
);

/** A zeroed counts object for every active vehicle type. */
export function createEmptyCounts(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const type of ACTIVE_VEHICLE_TYPES) counts[type.id] = 0;
  return counts;
}

export function vehicleTypeName(id: string): string {
  return VEHICLE_TYPE_BY_ID.get(id)?.name ?? id;
}

export function vehicleTypeCode(id: string): string {
  return VEHICLE_TYPE_BY_ID.get(id)?.code ?? id.toUpperCase();
}
